import {useEffect, useReducer, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {toast} from 'sonner';
import {Pencil, Plus, Trash2, RefreshCw} from 'lucide-react';
import {DiibsIcon} from '../../DiibsIcon';
import {useThemeMode} from '../../../../../_metronic/partials';
import DataTableWrapper from '../../../../DataTable';
import {API_ERROR_MESSAGE, TOAST_GENERAL} from '../../../../Helpers';
import {CustomModal} from '../../../../components/Modal';
import {CommonRowInTable, CommonViewInHeader} from '../../../../components/common/DataTableViews';
import {useAuth} from '../../../../modules/auth';
import reducerFunction from './actions';
import {TInitialState} from './index.d';

const initialState: TInitialState = {
  loading: true,
  pageSize: 10,
  data: [],
  currentPage: 1,
  totalPages: 1,
  totalData: -1,
  error: null,
  search: '',
  isModalOpen: false,
  selectedRow: null,
  capacity: '',
};

const TableDataTable = () => {
  const [state, dispatch] = useReducer(reducerFunction, initialState);
  const {mode} = useThemeMode();
  const {currentUser} = useAuth();
  const isAdmin = currentUser?.roleName === 'ADMIN' || currentUser?.role === 'ADMIN';
  const [searchParams] = useSearchParams();
  const restaurantId = searchParams.get('restaurant');
  const param = useParams();
  const {restaurantModule} = useAuth();

  // POS pull state
  const [posPulling, setPosPulling] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  let columns = [
    {
      name: <CommonViewInHeader name='Table No.' />,
      selector: (row, i) => row.tableNumber,
      sortable: true,
      format: (row, i) => (
        <CommonRowInTable
          text={row.tableNumber != null ? `Table ${row.tableNumber}` : '—'}
          classes={`${isAdmin ? 'text-theme-color' : 'text-dark'}`}
        />
      ),
    },
    {
      name: <CommonViewInHeader name='Capacity' />,
      selector: (row, i) => row.capacity,
      sortable: true,
      format: (row, i) => (
        <CommonRowInTable
          text={row.capacity + ' Persons'}
          classes={`${isAdmin ? 'text-theme-color' : 'text-dark'}`}
        />
      ),
    },
    {
      name: <CommonViewInHeader name='Source' />,
      selector: (row, i) => row.isPOSPulled,
      sortable: false,
      format: (row, i) =>
        row.isPOSPulled ? (
          <span className='badge badge-light-info fs-8 fw-semibold'>POS Pull</span>
        ) : (
          <span className='badge badge-light-secondary fs-8 fw-semibold'>Manual</span>
        ),
    },
    {
      name: <CommonViewInHeader name='Enabled' />,
      selector: (row, i) => row.isEnabled,
      sortable: false,
      format: (row, i) => (
        <div className='form-check form-switch'>
          <input
            className='form-check-input'
            type='checkbox'
            role='switch'
            checked={!!row.isEnabled}
            disabled={togglingId === row.id}
            onChange={() => handleToggleEnabled(row)}
          />
        </div>
      ),
    },
    {
      name: <CommonViewInHeader name='Action' />,
      selector: (row, i) => row.price,
      format: (row, i) => {
        return (
          <div className='d-flex align-items-center justify-content-center gap-2'>
            <span
              title='Edit table'
              className='linkIcon'
              onClick={() => {
                if (row?.deletable === 'false') {
                  toast.error(
                    'Can’t edit this table as it has ongoing auctions or upcoming reservation.'
                  );                } else {
                  dispatch({type: 'SELECT_ROW', payload: row});
                  dispatch({type: 'OPEN_MODAL', payload: 'TABLE'});
                }
              }}
            >
              <DiibsIcon icon={Pencil} size={24} />
            </span>

            <span
              title='Delete table'
              className='linkIcon'
              onClick={() => {
                if (row?.deletable === 'false') {
                  toast.error(
                    'Can’t delete this table as it has ongoing auctions or upcoming reservation.'
                  );
                } else {
                  dispatch({type: 'SELECT_ROW', payload: row});
                  dispatch({type: 'OPEN_MODAL', payload: 'DELETE'});
                }
              }}
            >
              <DiibsIcon icon={Trash2} size={24} />
            </span>
          </div>
        );
      },
      sortable: false,
    },
  ];

  columns = isAdmin
    ? [
        {
          name: <CommonViewInHeader name='Restaurant Name' classes='ms-8' />,
          selector: (row, i) => row.restaurants.name,
          sortable: true,
          format: (row, i) => (
            <CommonRowInTable
              text={row?.restaurants?.name}
              avatar={row?.restaurants?.images || 'media/avatars/restaurant-blank.png'}
              classes='text-dark'
            />
          ),
        },
        ...columns,
      ]
    : columns;

  const getTRestaurantTables = async () => {
    dispatch({type: 'LOADING', payload: true});
    // RESTAURANT-role: backend scopes by JWT (PDE-355). Only ADMIN sends restaurantIdFilter.
    const result = await restaurantModule?.getRestaurantTables({
      pageNo: state.currentPage,
      pageSize: state.pageSize,
      ...(isAdmin ? {restaurantIdFilter: restaurantId} : {}),
      capacity: state.capacity,
      includeDisabled: true,   // management page always sees all tables
    });
    if (result?.success) {
      dispatch({type: 'GET_DATA', payload: result?.data?.rows});
      dispatch({type: 'SET_TOTAL_PAGES', payload: result?.data?.count || 1});
    } else {
      dispatch({type: 'GET_DATA', payload: []});
      dispatch({type: 'SET_TOTAL_PAGES', payload: 1});
      if (result?.status !== 600) {
        toast.error(result?.message || API_ERROR_MESSAGE, {
          id: TOAST_GENERAL,
        });
      }
    }
  };

  useEffect(() => {
    getTRestaurantTables();
  }, [state.currentPage, restaurantId, state.capacity, state.pageSize]);

  const addTRestaurantTables = async (value) => {
    const result = await restaurantModule?.addRestaurantTables(
      isAdmin && param?.restaurantId,
      value
    );
    if (result?.success) {
      toast.success(result?.message || `Table added successfully`, {});
      dispatch({type: 'CLOSE_MODAL'});
      getTRestaurantTables();
    } else {
      toast.error(result?.message, {});
    }
  };

  const updateTRestaurantTables = async (value) => {
    const result = await restaurantModule?.updateRestaurantTables(value);
    if (result?.success) {
      toast.success(result?.message || `Table updated successfully`, {});
      getTRestaurantTables();
      dispatch({type: 'CLOSE_MODAL'});
    } else {
      toast.error(result?.message, {});
    }
  };

  const handleToggleEnabled = async (row) => {
    setTogglingId(row.id);
    const result = await restaurantModule?.toggleTableEnabled(row.id, !row.isEnabled);
    if (result?.success) {
      toast.success(`Table ${row.tableNumber ?? ''} ${!row.isEnabled ? 'enabled' : 'disabled'}.`);
      getTRestaurantTables();
    } else {
      toast.error(result?.message || 'Failed to update table.');
    }
    setTogglingId(null);
  };

  // Detect connected POS vendor from current user context (if available)
  // Falls back to trying clover first, then square.
  const handlePOSPull = async () => {
    setPosPulling(true);
    // Try the vendors that are likely connected; the backend will error gracefully
    // if neither is connected. We try both and show the first success.
    const vendors = ['clover', 'square'];
    let pulled = false;
    for (const vendor of vendors) {
      const result = await restaurantModule?.pullPOSTables(vendor, 30);
      if (result?.success) {
        const d = result.data as {inserted: number; updated: number; skipped: number};
        toast.success(
          `Pulled tables from ${vendor.charAt(0).toUpperCase() + vendor.slice(1)}: ` +
            `${d?.inserted ?? 0} new, ${d?.updated ?? 0} updated, ${d?.skipped ?? 0} skipped.`,
          {duration: 7000}
        );
        pulled = true;
        getTRestaurantTables();
        break;
      }
    }
    if (!pulled) {
      toast.error('No POS connection found. Connect a POS first from Connect POS.', {duration: 6000});
    }
    setPosPulling(false);
  };

  const subHeader = (
    <div className='d-flex gap-2'>
      {!isAdmin && (
        <button
          type='button'
          className='btn btn-info'
          onClick={handlePOSPull}
          disabled={posPulling}
        >
          {posPulling ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' role='status' />
              Pulling…
            </>
          ) : (
            <>
              <DiibsIcon icon={RefreshCw} size={20} />
              POS Pull
            </>
          )}
        </button>
      )}
      <button
        type='button'
        className='btn btn-primary'
        onClick={() => dispatch({type: 'OPEN_MODAL', payload: 'TABLE'})}
      >
        <DiibsIcon icon={Plus} size={20}/>
        Add Table
      </button>
    </div>
  );

  const deleteTTable = async (tableIds) => {
    const result = await restaurantModule?.deleteTables(tableIds);
    dispatch({type: 'CLOSE_MODAL'});
    if (result?.success) {
      toast.success(`Table deleted succesfully`, {
        id: TOAST_GENERAL,
      });
      getTRestaurantTables();
    } else {
      toast.error(result?.message || API_ERROR_MESSAGE, {
        id: TOAST_GENERAL,
      });
    }
  };

  return (
    <>
      <DataTableWrapper
        totalData={state.totalData}
        subHeader={subHeader}
        pageSize={state.pageSize}
        selectableRows={true}
        loading={state.loading}
        columns={columns}
        handleSelected={(selected) => {
          dispatch({type: 'SELECT_ROW', payload: selected});
          dispatch({type: 'OPEN_MODAL', payload: 'DELETE'});
        }}
        data={state.data}
        currentPage={state.currentPage}
        totalPages={state.totalPages}
        handlePageChange={(page) => dispatch({type: 'PAGE_CHANGE', payload: page})}
        handlePageSizeChange={(pageSize) => dispatch({type: 'SET_PAGE_SIZE', payload: +pageSize})}
        filters={{capacity: true}}
        filtersCallback={(value) => {
          dispatch({type: 'SET_CAPACITY', payload: value?.capacity});
        }}
        isFilterApplied={Boolean(state.capacity)}
      />
      {state.isModalOpen && (
        <CustomModal
          title={'Add Table'}
          data={state.selectedRow}
          handleCancelCallback={() => dispatch({type: 'CLOSE_MODAL'})}
          optionalText={
            state?.selectedRow?.hasRecurring
              ? 'Are you sure you want to delete this table? It currently has recurring auctions scheduled. If you proceed, then recurring auction for this table will also be deleted.'
              : ''
          }
          handleSaveCallback={async (value) => {
            if (state.modalType === 'DELETE') {
              const ids = Array.isArray(state.selectedRow)
                ? state.selectedRow.map((row) => row.id)
                : [state?.selectedRow?.id];
              await deleteTTable({ids}); // await so the refresh inside fires before unmounting
            } else if (state.selectedRow) {
              await updateTRestaurantTables(value);
            } else {
              let restaurantData = value;
              if (restaurantId) {
                restaurantData = {...value, restaurantId};
              }
              await addTRestaurantTables(restaurantData);
            }
          }}
          type={state.modalType}
        />
      )}
    </>
  );
};

export default TableDataTable;
